import React, { useRef, useState } from 'react';
import { ArrowLeft, Download, Share2, Eye, TrendingUp, DollarSign, BarChart3, LightbulbIcon } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { BENCHMARKS } from '../constants/benchmarks';
import { MetricTooltip } from './MetricTooltip';
import { MetricCard } from './MetricCard';
import { Recommendations } from './Recommendations';
import { formatCurrency, calculateROI, getConversionPerformanceLevel, getROIPerformanceLevel, getCPLPerformanceLevel, getCACTicketRatioPerformanceLevel } from '../utils/performanceMetrics';
import { ProjectionsModal } from './ProjectionsModal';
import { generateProjections } from '../utils/projectionCalculations';

interface AnalysisResultsProps {
  formData: {
    leadsPerMonth: number;
    conversionRate: number;
    averageProjectValue: number;
    marketingInvestment: number;
    acquisitionChannels: string[];
    totalAcquisitionCosts: number;
    newClientsPerMonth: number;
    totalProjectsValue: number;
    totalProjects: number;
  };
}

export default function AnalysisResults({ formData }: AnalysisResultsProps) {
  const [showProjections, setShowProjections] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const metricsRef = useRef<HTMLDivElement>(null);
  const recommendationsRef = useRef<HTMLDivElement>(null);

  const monthlyRevenue = formData.averageProjectValue * formData.newClientsPerMonth;
  const roi = calculateROI(monthlyRevenue, formData.marketingInvestment);
  const costPerLead = formData.marketingInvestment / formData.leadsPerMonth;
  const cplPerformance = getCPLPerformanceLevel(costPerLead);
  const conversionPerformance = getConversionPerformanceLevel(formData.conversionRate);
  const roiPerformance = getROIPerformanceLevel(roi);
  
  const cac = formData.totalAcquisitionCosts / formData.newClientsPerMonth;
  const averageTicket = formData.totalProjectsValue / formData.totalProjects;
  const cacTicketRatio = averageTicket / cac;
  const cacTicketPerformance = getCACTicketRatioPerformanceLevel(cacTicketRatio);

  const metricsData = [
    { name: 'Receita Mensal', value: monthlyRevenue },
    { name: 'CAC', value: cac },
    { name: 'Ticket Médio', value: averageTicket },
  ];

  const handleDownloadPDF = async () => {
    if (!metricsRef.current || !recommendationsRef.current) return;

    setIsDownloading(true);

    try {
      // Captura a seção de métricas
      const metricsCanvas = await html2canvas(metricsRef.current, {
        scale: 2,
        logging: false,
        useCORS: true,
        allowTaint: true,
        scrollY: -window.scrollY,
      });

      // Captura a seção de recomendações
      const recommendationsCanvas = await html2canvas(recommendationsRef.current, {
        scale: 2,
        logging: false,
        useCORS: true,
        allowTaint: true,
        scrollY: -window.scrollY,
      });

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = 210;
      const pageHeight = 297;
      const margin = 10;

      // Primeira página - Métricas
      pdf.setFontSize(16);
      pdf.text('Análise de Métricas - Resultados', pageWidth / 2, margin + 5, { align: 'center' });
      
      pdf.setFontSize(10);
      pdf.setTextColor(128, 128, 128);
      const date = new Date().toLocaleDateString('pt-BR');
      pdf.text(`Gerado em: ${date}`, margin, margin + 15);
      
      pdf.setTextColor(0, 0, 0);
      
      const metricsWidth = pageWidth - (margin * 2);
      const metricsHeight = (metricsCanvas.height * metricsWidth) / metricsCanvas.width;
      
      pdf.addImage(
        metricsCanvas.toDataURL('image/png'),
        'PNG',
        margin,
        margin + 20,
        metricsWidth,
        metricsHeight,
        '',
        'FAST'
      );

      // Segunda página - Recomendações
      pdf.addPage();
      
      pdf.setFontSize(16);
      pdf.text('Recomendações', pageWidth / 2, margin + 5, { align: 'center' });
      
      const recommendationsWidth = pageWidth - (margin * 2);
      const recommendationsHeight = (recommendationsCanvas.height * recommendationsWidth) / recommendationsCanvas.width;
      
      pdf.addImage(
        recommendationsCanvas.toDataURL('image/png'),
        'PNG',
        margin,
        margin + 20,
        recommendationsWidth,
        recommendationsHeight,
        '',
        'FAST'
      );

      pdf.save('analise-metricas.pdf');
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <a href="/metrics" className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Nova análise
          </a>
          <div className="flex gap-3">
            <button
              onClick={handleDownloadPDF}
              disabled={isDownloading}
              className="inline-flex items-center px-4 py-2 text-sm text-gray-700 bg-white rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isDownloading ? 'Gerando PDF...' : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Baixar PDF
                </>
              )}
            </button>
            <button className="inline-flex items-center px-4 py-2 text-sm text-gray-700 bg-white rounded-lg border border-gray-300 hover:bg-gray-50">
              <Share2 className="h-4 w-4 mr-2" />
              Compartilhar
            </button>
            <button 
              onClick={() => setShowProjections(true)}
              className="inline-flex items-center px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700"
            >
              <Eye className="h-4 w-4 mr-2" />
              Ver Projeções
            </button>
          </div>
        </div>

        <div className="space-y-8">
          {/* Seção de Métricas */}
          <div ref={metricsRef} className="bg-white rounded-lg shadow-md p-6">
            <h1 className="text-xl font-bold text-gray-900 mb-6">Resultados da Análise</h1>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <MetricCard
                title="Taxa de Conversão"
                value={`${formData.conversionRate.toFixed(1)}%`}
                performance={conversionPerformance}
                description={BENCHMARKS.conversion.description}
              />
              <MetricCard
                title="ROI do Marketing"
                value={`${roi.toFixed(1)}%`}
                performance={roiPerformance}
                description={BENCHMARKS.roi.description}
              />
              <MetricCard
                title="Custo por Lead"
                value={formatCurrency(costPerLead)}
                performance={cplPerformance}
                description={BENCHMARKS.costPerLead.description}
              />
            </div>

            <hr className="my-8 border-t border-gray-200" />

            <h2 className="text-lg font-semibold text-gray-900 mb-4">Análise de CAC e Ticket Médio</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="bg-[#f5f5f5] p-4 rounded-lg shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1">
                    <span className="text-sm text-gray-600">CAC (Custo de Aquisição)</span>
                    <MetricTooltip description="Custo total para adquirir um novo cliente" />
                  </div>
                </div>
                <div className="text-2xl font-bold">{formatCurrency(cac)}</div>
              </div>
              <div className="bg-[#f5f5f5] p-4 rounded-lg shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1">
                    <span className="text-sm text-gray-600">Ticket Médio</span>
                    <MetricTooltip description="Valor médio por projeto" />
                  </div>
                </div>
                <div className="text-2xl font-bold">{formatCurrency(averageTicket)}</div>
              </div>
              <div className="bg-[#f5f5f5] p-4 rounded-lg shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1">
                    <span className="text-sm text-gray-600">Relação Ticket/CAC</span>
                    <MetricTooltip description={BENCHMARKS.cacTicketRatio.description} />
                  </div>
                  {cacTicketPerformance.icon}
                </div>
                <div className={`text-2xl font-bold ${cacTicketPerformance.color}`}>
                  {cacTicketRatio.toFixed(1)}x
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${cacTicketPerformance.color} bg-white/80`}>
                  {cacTicketPerformance.level}
                </span>
              </div>
            </div>

            <h2 className="text-lg font-semibold text-gray-900 mb-4">Análise Gráfica</h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={metricsData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Bar dataKey="value" fill="#000" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Seção de Recomendações */}
          <div ref={recommendationsRef} className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              <LightbulbIcon className="h-5 w-5 inline-block mr-2 text-yellow-500" />
              Recomendações
            </h2>
            <Recommendations metrics={{
              roi,
              costPerLead,
              cacTicketRatio,
              conversionRate: formData.conversionRate,
              acquisitionChannels: formData.acquisitionChannels,
            }} />
          </div>
        </div>

        {showProjections && (
          <ProjectionsModal
            data={generateProjections(formData)}
            onClose={() => setShowProjections(false)}
          />
        )}
      </div>
    </div>
  );
}